<template>
  <div
    v-if="customIsVisible && (fetchingProducts || products.length > 0)"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="banner-with-products"
  >
    <div v-if="fetchingProducts" class="mx-auto w-full max-w-screen-2xl px-5 md:px-12 animate-pulse">
      <InnoBannerWithProductsSkeleton
        :columns-amount-tablet="columnsAmountTablet"
        :columns-amount-desktop="columnsAmountDesktop"
      />
    </div>
    <div v-else class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <VcTypography v-if="title" :tag="heading" :anchor-id="title" class="mt-4 mb-8">
        {{ title }}
      </VcTypography>
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-10">
        <!-- Banner -->
        <div
          class="relative min-h-[500px] w-full rounded-lg bg-center bg-cover bg-[image:var(--banner-image-default)] xs:bg-[image:var(--banner-image-landscape)] lg:bg-[image:var(--banner-image-default)] lg:w-[290px] lg:min-w-[290px] lg:flex-none"
          :style="bannerBackgroundStyle"
        >
          <div
            class="absolute w-full h-full rounded-lg bg-cover z-[1] bg-gradient-to-b from-transparent to-primary-950"
          ></div>
          <div class="absolute flex flex-col rounded-lg justify-end h-full z-[2] p-6">
            <div>
              <VcTypography v-if="bannerTitle" tag="h3" font-weight="700" class="text-white">
                {{ bannerTitle }}
              </VcTypography>
            </div>
            <div v-if="bannerSubtitle" class="mt-3 text-white">{{ bannerSubtitle }}</div>
            <div v-if="buttonLabel && buttonLink" class="flex mt-6 mb-2">
              <InnoButton
                shape="pill"
                v-bind="getLinkAttr(buttonLink)"
                :target="'externalLink' in getLinkAttr(buttonLink) ? '_blank' : '_self'"
                class="flex-1 lg:flex-none w-full"
              >
                {{ buttonLabel }}
              </InnoButton>
            </div>
          </div>
        </div>

        <!-- Products -->
        <div class="lg:w-auto">
          <div
            :class="[
              'grid grid-cols-1 gap-6 xs:grid-cols-2 lg:gap-5',
              `md:grid-cols-${columnsAmountTablet}`,
              `lg:grid-cols-${columnsAmountDesktop}`,
            ]"
          >
            <InnoItem
              v-for="item in sortedProducts"
              :key="item.id"
              :product="item"
              :background-color="backgroundColor"
            ></InnoItem>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getLinkAttr } from "@/core/utilities/common";
import { computed, ref, watchEffect } from "vue";
import { useProducts } from "@/shared/catalog";
import InnoButton from "@/ui-kit/components/molecules/innovadis/inno-button.vue";
import InnoItem from "./inno-banner-with-products-item.vue";
import InnoBannerWithProductsSkeleton from "./inno-banner-with-products-skeleton.vue";
import { useAnalytics } from "@/core/composables/useAnalytics";

type ProductSkuType = {
  productSku: string;
};

interface IProps {
  id: string;
  heading?: string;
  title?: string;
  image: string;
  imageLandscape?: string;
  imageDescription: string;
  componentTitle?: string;
  bannerTitle?: string;
  bannerSubtitle?: string;
  buttonLabel?: string;
  buttonLink?: string;
  skus?: ProductSkuType[];
  count?: number;
  query?: string;
  filter?: string;
  columnsAmountDesktop?: string;
  columnsAmountTablet?: string;

  background?: string;
  model: any;
  settings: any;

  // Innovadis custom: Use custom visibility setting
  customIsVisible: boolean;

  // Innovadis custom: Use custom layout settings
  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
  count: 8,
  columnsAmountDesktop: "4",
  columnsAmountTablet: "3",
});

const { products, fetchProducts, fetchingProducts } = useProducts();
const { analytics } = useAnalytics();
const sortedProducts = ref<any[]>([]);

const bannerBackgroundStyle = computed(() => ({
  "--banner-image-landscape": `url(${props.imageLandscape || props.image})`,
  "--banner-image-default": `url(${props.image})`,
}));

watchEffect(async () => {
  if (props.skus && props.skus.length > 0) {
    // Remove empty sku entries
    const productSkus = props.skus
      .map((sku) => sku.productSku)
      .filter((sku) => sku && sku.trim() !== "")
      .join(",");

    // Fetch products based on the filtered SKUs
    await fetchProducts({ filter: `"SKU":${productSkus}` });

    // Access the value of the computed products array
    const productList = products.value;

    // Reorder products based on the order of props.skus
    // The result of fetchProducts is kinda random
    const sorted = props.skus
      .map((sku) => {
        return productList.find((product: any) => product.code === sku.productSku); // Find product by code
      })
      .filter((product) => product !== undefined); // Filter out any undefined products

    // Update the sortedProducts ref with the new order
    sortedProducts.value = sorted;

    // push to google
    analytics("viewItemList", sortedProducts.value, {
      item_list_id: "banner_with_products",
      item_list_name: props.title ? props.title : props.bannerTitle ? props.bannerTitle : "Banner with products",
    });
  } else {
    await fetchProducts({
      itemsPerPage: props.count,
      keyword: props.query,
      filter: props.filter,
    });
  }
});
</script>
